import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getReports, exportReportsPdf, formatAmount } from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const CARD_BG  = { background: '#1C1C1E', border: '1px solid #2C2C2E' };
const CHIP_BG  = { background: '#2C2C2E' };
const DIM      = { color: '#8E8E93' };
const DIMMER   = { color: '#636366' };

function todayIST() {
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  return ist.toISOString().split('T')[0];
}

function fmtDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function typeLabel(et) {
  const map = {
    sale: 'Sale', invoice: 'Invoice', payment: 'Payment',
    purchase: 'Purchase', expense: 'Expense', trip: 'Trip/Delivery',
    stock_update: 'Stock In', production: 'Production',
  };
  return map[et] || (et || '—').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function typeColor(et) {
  if (['sale', 'invoice'].includes(et))     return '#30D158';
  if (et === 'payment')                      return '#0A84FF';
  if (['expense', 'purchase'].includes(et)) return '#FF453A';
  if (et === 'trip')                         return '#FF9F0A';
  return '#8E8E93';
}

function exportCSV(transactions, date) {
  const headers = ['Date','Time','Entry Type','Party Name','Item','Quantity','Unit','Amount','Submitted By','Role','Status'];
  const rows = transactions.map(t => [
    date, t.time, t.entry_type, t.party_name, t.item_name,
    t.quantity ?? '', t.unit, t.total_amount ?? '',
    t.submitted_by, t.submitted_by_role, t.status,
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `report_${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ReportsPage = () => {
  const { business } = useAuth();
  const navigate = useNavigate();

  const [date, setDate]               = useState(todayIST);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState('transactions');

  const load = useCallback(async (d) => {
    if (!business?.id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getReports(business.id, d);
      setData(res.data);
    } catch (err) {
      setError('Could not load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => { load(date); }, [business, date, load]);

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    const nd = d.toISOString().split('T')[0];
    setDate(nd);
  };

  const summary     = data?.daily_summary || {};
  const txns        = data?.transactions || [];
  const parties     = data?.party_summary || [];
  const empAct      = data?.employee_activity || [];
  const workerAct   = data?.worker_activity || [];
  const plSummary   = data?.pl_summary || {};
  const tripRate    = data?.trip_rate ?? 350;

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'transparent transparent #FF2D55 transparent' }} />
      </div>
    );
  }

  const isToday = date === todayIST();

  return (
    <div className="min-h-screen text-white" data-testid="reports-page"
      style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>

      {/* ── Header ── */}
      <header className="p-4 sticky top-0 z-10"
        style={{ background: 'rgba(0,0,0,.9)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>

          {/* Date navigator */}
          <div className="flex items-center gap-2 flex-1">
            <button onClick={() => changeDate(-1)}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/10" data-testid="prev-date-btn">
              <svg className="w-4 h-4" fill="none" stroke="#8E8E93" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="text-center flex-1">
              <p className="text-base font-semibold">{isToday ? 'Today' : fmtDate(date)}</p>
              {!isToday && <p className="text-xs" style={DIM}>{date}</p>}
            </div>
            <button onClick={() => changeDate(1)} disabled={isToday}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/10 disabled:opacity-30" data-testid="next-date-btn">
              <svg className="w-4 h-4" fill="none" stroke="#8E8E93" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Export buttons */}
          <div className="flex gap-1.5">
            <button onClick={() => txns.length && exportCSV(txns, date)}
              disabled={!txns.length}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-white/10 disabled:opacity-30"
              style={{ border: '1px solid #2C2C2E', color: '#8E8E93' }}
              data-testid="export-csv-btn">CSV</button>
            <a href={business?.id ? exportReportsPdf(business.id, date) : '#'}
              target="_blank" rel="noreferrer"
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-white/10"
              style={{ border: '1px solid #2C2C2E', color: '#8E8E93' }}
              data-testid="export-pdf-btn">PDF</a>
          </div>

          <button onClick={() => load(date)}
            className="p-2 rounded-full transition-colors hover:bg-white/10" data-testid="refresh-btn">
            <svg className="w-4 h-4" fill="none" stroke="#8E8E93" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-80px)]">
        <main className="p-4 space-y-4" data-testid="reports-content">

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: 'rgba(255,69,58,.1)', border: '1px solid rgba(255,69,58,.2)' }}>
              <span className="text-xs" style={{ color: '#FF453A' }}>{error}</span>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2"
                style={{ borderColor: 'transparent transparent #FF2D55 transparent' }} />
            </div>
          )}

          {/* ── Summary Cards ── */}
          <div className="grid grid-cols-2 gap-3" data-testid="summary-cards">
            {[
              { label: 'Sales',     val: summary.total_sales,    color: '#30D158' },
              { label: 'Collected', val: summary.total_payments, color: '#0A84FF' },
              { label: 'Expenses',  val: summary.total_expenses, color: '#FF453A' },
              { label: 'Net',       val: summary.net_position,   color: '#FF9F0A' },
            ].map(({ label, val, color }) => (
              <div key={label} className="rounded-2xl p-4 text-center" style={CARD_BG} data-testid={`card-${label.toLowerCase()}`}>
                <p className="text-xl font-bold" style={{ color }}>{formatAmount(val || 0)}</p>
                <p className="text-xs mt-1" style={DIM}>{label}</p>
              </div>
            ))}
          </div>

          {/* Entry count pill */}
          {data && (
            <div className="text-center">
              <span className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ ...CHIP_BG, color: '#8E8E93' }}>
                {summary.entry_count || 0} entries
              </span>
            </div>
          )}

          {/* ── Tabs ── */}
          <div className="flex gap-1 p-1 rounded-xl" style={CHIP_BG}>
            {[
              { key: 'transactions', label: 'Transactions' },
              { key: 'parties',      label: 'Parties' },
              { key: 'team',         label: 'Team' },
              { key: 'pl',           label: 'P&L' },
            ].map(({ key, label }) => (
              <button key={key}
                onClick={() => setActiveTab(key)}
                className="flex-1 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: activeTab === key ? '#3A3A3C' : 'transparent',
                  color: activeTab === key ? '#fff' : '#8E8E93',
                }}
                data-testid={`tab-${key}`}>
                {label}
              </button>
            ))}
          </div>

          {/* ── Transactions Tab ── */}
          {activeTab === 'transactions' && (
            <div data-testid="transactions-section">
              {txns.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <p className="font-medium" style={DIM}>No entries for this date</p>
                  <p className="text-sm mt-1" style={DIMMER}>Send a WhatsApp message to log entries</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {txns.map((t) => (
                    <div key={t.id} className="rounded-2xl p-4"
                      style={{ ...CARD_BG, borderLeft: `3px solid ${typeColor(t.entry_type)}` }}
                      data-testid="transaction-row">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ color: typeColor(t.entry_type), background: `${typeColor(t.entry_type)}20` }}>
                              {typeLabel(t.entry_type)}
                            </span>
                            <span className="text-xs" style={DIMMER}>{t.time}</span>
                          </div>
                          {t.party_name && (
                            <p className="text-sm font-medium text-white truncate">{t.party_name}</p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            {t.item_name && <span className="text-xs" style={DIM}>{t.item_name}</span>}
                            {t.quantity && t.item_name && <span className="text-xs" style={DIMMER}>•</span>}
                            {t.quantity && (
                              <span className="text-xs" style={DIM}>{t.quantity} {t.unit}</span>
                            )}
                          </div>
                          {t.submitted_by && (
                            <p className="text-xs mt-1" style={DIMMER}>
                              By {t.submitted_by}
                              {t.submitted_by_role !== 'owner' && (
                                <span className="ml-1 px-1.5 py-0.5 rounded" style={{ background: '#2C2C2E', color: '#8E8E93' }}>
                                  {t.submitted_by_role}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          {t.total_amount != null ? (
                            <p className="text-base font-bold" style={{ color: typeColor(t.entry_type) }}>
                              {formatAmount(t.total_amount)}
                            </p>
                          ) : null}
                          <span className="text-xs px-1.5 py-0.5 rounded"
                            style={{ background: '#2C2C2E', color: '#636366' }}>
                            {t.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Parties Tab ── */}
          {activeTab === 'parties' && (
            <div data-testid="parties-section">
              {parties.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <p className="font-medium" style={DIM}>No party data yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {parties.map((p) => (
                    <div key={p.party_name} className="rounded-2xl p-4" style={CARD_BG}
                      data-testid="party-row">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-white">{p.party_name}</p>
                        <p className="text-base font-bold"
                          style={{ color: p.outstanding > 0 ? '#FF453A' : '#30D158' }}>
                          {p.outstanding > 0 ? `${formatAmount(p.outstanding)} due` : 'Settled'}
                        </p>
                      </div>
                      <div className="flex gap-4 mt-2">
                        <div>
                          <p className="text-xs" style={DIMMER}>Sales</p>
                          <p className="text-sm font-medium" style={{ color: '#30D158' }}>{formatAmount(p.total_sales)}</p>
                        </div>
                        <div>
                          <p className="text-xs" style={DIMMER}>Received</p>
                          <p className="text-sm font-medium" style={{ color: '#0A84FF' }}>{formatAmount(p.total_payments)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Team Tab ── */}
          {activeTab === 'team' && (
            <div data-testid="team-section">
              {/* Worker wage settlement cards (trips/wages by party_name) */}
              {workerAct.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-medium px-1" style={DIM}>Worker Settlement</p>
                  {workerAct.map((w) => (
                    <div key={w.worker_name} className="rounded-2xl p-4" style={CARD_BG}
                      data-testid="worker-settlement-row">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-white">{w.worker_name}</p>
                          {w.attendance && (
                            <span className="text-xs px-1.5 py-0.5 rounded mt-0.5 inline-block"
                              style={{
                                background: w.attendance === 'present' ? 'rgba(48,209,88,.15)' : 'rgba(255,69,58,.15)',
                                color: w.attendance === 'present' ? '#30D158' : '#FF453A',
                              }}>
                              {w.attendance}
                            </span>
                          )}
                          <div className="flex gap-4 mt-2">
                            {w.trips_count > 0 && (
                              <div>
                                <p className="text-xs" style={DIMMER}>Trips</p>
                                <p className="text-sm font-medium" style={{ color: '#FF9F0A' }}>
                                  {w.trips_count} × ₹{w.trip_rate?.toLocaleString('en-IN')}
                                </p>
                              </div>
                            )}
                            {w.expected_wage > 0 && (
                              <div>
                                <p className="text-xs" style={DIMMER}>Due</p>
                                <p className="text-sm font-medium text-white">
                                  {formatAmount(w.expected_wage)}
                                </p>
                              </div>
                            )}
                            {w.wages_earned > 0 && (
                              <div>
                                <p className="text-xs" style={DIMMER}>Paid</p>
                                <p className="text-sm font-medium" style={{ color: '#0A84FF' }}>
                                  {formatAmount(w.wages_earned)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <span className="text-xs font-semibold px-2 py-1 rounded-lg"
                            style={{
                              background: w.wage_settled ? 'rgba(48,209,88,.15)' : 'rgba(255,159,10,.15)',
                              color: w.wage_settled ? '#30D158' : '#FF9F0A',
                            }}
                            data-testid={`wage-status-${w.worker_name}`}>
                            {w.wage_settled ? 'Settled' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Submitter activity cards */}
              {empAct.length === 0 && workerAct.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <p className="font-medium" style={DIM}>No team activity for this date</p>
                </div>
              ) : empAct.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium px-1" style={DIM}>Submissions</p>
                  {empAct.map((e) => (
                    <div key={e.employee_name} className="rounded-2xl p-4" style={CARD_BG}
                      data-testid="team-row">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{e.employee_name}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                            style={{ background: '#2C2C2E', color: '#8E8E93' }}>
                            {e.role}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-base font-bold text-white">{e.entry_count} entries</p>
                          {e.total_value > 0 && (
                            <p className="text-sm" style={{ color: '#30D158' }}>{formatAmount(e.total_value)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* ── P&L Tab (Features 27 + 28) ── */}
          {activeTab === 'pl' && (
            <div data-testid="pl-section" className="space-y-3">

              {/* Revenue vs Expenses */}
              <div className="rounded-2xl p-4 space-y-3" style={CARD_BG}>
                <p className="text-xs font-semibold uppercase tracking-wider" style={DIM}>
                  Profit & Loss
                </p>
                {[
                  { label: 'Sales Recorded',     val: plSummary.revenue,           color: '#30D158' },
                  { label: 'Cash Received',       val: plSummary.payments_received, color: '#0A84FF' },
                  { label: 'Expenses',            val: plSummary.expenses,          color: '#FF453A' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="flex items-center justify-between py-1"
                    style={{ borderBottom: '1px solid #2C2C2E' }}>
                    <p className="text-sm" style={DIM}>{label}</p>
                    <p className="text-sm font-semibold" style={{ color }}>{formatAmount(val || 0)}</p>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-sm font-semibold text-white">Net (Received − Expenses)</p>
                  <p className="text-base font-bold"
                    style={{ color: (plSummary.net || 0) >= 0 ? '#30D158' : '#FF453A' }}
                    data-testid="pl-net">
                    {formatAmount(plSummary.net || 0)}
                  </p>
                </div>
              </div>

              {/* GST Estimate (Feature 28) */}
              <div className="rounded-2xl p-4 space-y-3" style={CARD_BG}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={DIM}>
                    GST @12% <span style={{ color: '#FF9F0A', fontStyle: 'italic' }}>(estimated)</span>
                  </p>
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,159,10,.15)', color: '#FF9F0A' }}>
                    Not filed
                  </span>
                </div>
                <p className="text-xs" style={DIMMER}>
                  Taxable value: {formatAmount(plSummary.revenue || 0)} (sales)
                </p>
                {[
                  { label: 'CGST @6%', val: plSummary.cgst_6pct },
                  { label: 'SGST @6%', val: plSummary.sgst_6pct },
                ].map(({ label, val }) => (
                  <div key={label} className="flex items-center justify-between py-1"
                    style={{ borderBottom: '1px solid #2C2C2E' }}>
                    <p className="text-sm" style={DIM}>{label}</p>
                    <p className="text-sm font-semibold text-white">{formatAmount(val || 0)}</p>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-sm font-semibold text-white">Total GST @12%</p>
                  <p className="text-base font-bold" style={{ color: '#FF9F0A' }}
                    data-testid="pl-gst">
                    {formatAmount(plSummary.gst_on_sales_12pct || 0)}
                  </p>
                </div>
                <p className="text-xs mt-2" style={{ color: '#48484A', fontStyle: 'italic' }}>
                  GST @12% (estimated) — for reference only. Consult your CA for actual filing.
                </p>
              </div>

              {!data && (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <p className="font-medium" style={DIM}>No data for this date</p>
                </div>
              )}
            </div>
          )}

        </main>
      </ScrollArea>
    </div>
  );
};

export default ReportsPage;
